import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  query,
} from "firebase/firestore";
import { db } from "../firebase";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Heading } from "./heading";
import { Button } from "./button";
import { Input } from "./input";
import {
  Dialog,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogActions,
} from "./dialog";
import { showAlert } from "../alertService";
import SectionShell from "./SectionShell";
import {
  Cog6ToothIcon,
  BuildingOffice2Icon,
  ShoppingBagIcon,
  HandRaisedIcon,
  TruckIcon,
  MapIcon,
  DocumentIcon,
  InformationCircleIcon,
  UsersIcon,
  BanknotesIcon,
  LockClosedIcon,
  Bars3Icon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/20/solid";

// Each catalog declares which venue fields hold references to its items.
// Used by the hard-delete safety check.
//   arrayFields: venues/{id}[field] is `[itemId, ...]`
//   objectFields: venues/{id}[field] is `{ itemId: value }`
// Catálogos con `soloEtiquetas: true` son de tipo "valor": los venues guardan
// VALORES por item id (via CatalogValueFields) y los items llevan campos extra
// como `dataType`. Renombrar ids, eliminar, reordenar o desactivar items
// corrompería datos de venues — solo se permite editar las etiquetas ES/EN.
const CATALOGS = [
  {
    name: "servicios",
    label: "Servicios",
    icon: Cog6ToothIcon,
    arrayFields: [
      "serviciosIncluidos",
      "serviciosOpcionales",
      "serviciosExclusivos",
      "serviciosExternos",
    ],
    objectFields: [],
  },
  {
    name: "instalaciones",
    label: "Instalaciones",
    icon: BuildingOffice2Icon,
    arrayFields: ["instalaciones"],
    objectFields: [],
  },
  {
    name: "alimentosBebidas",
    label: "Alimentos y Bebidas",
    icon: ShoppingBagIcon,
    arrayFields: ["alimentosBebidas"],
    objectFields: [],
  },
  {
    name: "areasAdicionales",
    label: "Áreas Adicionales",
    icon: HandRaisedIcon,
    arrayFields: ["areasAdicionales"],
    objectFields: [],
  },
  {
    name: "estacionamiento",
    label: "Estacionamiento",
    icon: TruckIcon,
    arrayFields: [],
    objectFields: ["estacionamiento"],
  },
  {
    name: "transporteTerrestre",
    label: "Transporte Terrestre",
    icon: MapIcon,
    arrayFields: ["transporteTerrestre"],
    objectFields: [],
  },
  {
    name: "documentos",
    label: "Documentos",
    icon: DocumentIcon,
    arrayFields: [],
    objectFields: ["documentos"],
  },
  {
    name: "basicInfo",
    label: "Información Básica",
    icon: InformationCircleIcon,
    arrayFields: [],
    objectFields: ["basicInfo"],
    soloEtiquetas: true,
  },
  {
    name: "capacityFields",
    label: "Capacidades",
    icon: UsersIcon,
    arrayFields: [],
    objectFields: ["capacityFields"],
    soloEtiquetas: true,
  },
  {
    name: "politicaPagos",
    label: "Política de Pagos",
    icon: BanknotesIcon,
    arrayFields: [],
    objectFields: ["politicaPagos"],
    soloEtiquetas: true,
  },
];

const slugifyKey = (str) =>
  (str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 50);

const nextNumericId = (items) => {
  const max = items.reduce((m, it) => {
    const n = Number(it.id);
    return Number.isFinite(n) && n > m ? n : m;
  }, 0);
  return String(max + 1);
};

const isItemActivo = (item) => item?.activo !== false;

const itemPath = (catalogName, itemId) =>
  doc(db, "catalogos", catalogName, "items", itemId);

const itemsCollection = (catalogName) =>
  collection(db, "catalogos", catalogName, "items");

// Search all venues for any reference to the given catalog item id.
// Returns an array of { venueId, venueName, fields: [fieldName, ...] }.
async function findVenueReferences(catalog, itemId) {
  const numericId = Number(itemId);
  const venuesSnap = await getDocs(collection(db, "venues"));
  const refs = [];
  venuesSnap.forEach((venueDoc) => {
    const data = venueDoc.data() || {};
    const matchingFields = [];
    for (const field of catalog.arrayFields) {
      const arr = data[field];
      if (Array.isArray(arr) && arr.some((v) => Number(v) === numericId)) {
        matchingFields.push(field);
      }
    }
    for (const field of catalog.objectFields) {
      const obj = data[field];
      if (obj && typeof obj === "object" && obj[itemId] != null && obj[itemId] !== "") {
        matchingFields.push(field);
      }
    }
    if (matchingFields.length > 0) {
      refs.push({
        venueId: venueDoc.id,
        venueName: data.name || venueDoc.id,
        fields: matchingFields,
      });
    }
  });
  return refs;
}

const CatalogItemRow = ({
  item,
  catalogName,
  index,
  onRequestDelete,
  soloEtiquetas = false,
}) => {
  const [editingES, setEditingES] = useState(false);
  const [editingEN, setEditingEN] = useState(false);
  const [esDraft, setEsDraft] = useState(item.ES || "");
  const [enDraft, setEnDraft] = useState(item.EN || "");

  useEffect(() => {
    if (!editingES) setEsDraft(item.ES || "");
  }, [item.ES, editingES]);
  useEffect(() => {
    if (!editingEN) setEnDraft(item.EN || "");
  }, [item.EN, editingEN]);

  const activo = isItemActivo(item);

  const saveES = async () => {
    const trimmed = esDraft.trim();
    setEditingES(false);
    if (!trimmed || trimmed === (item.ES || "")) {
      setEsDraft(item.ES || "");
      return;
    }
    try {
      await updateDoc(itemPath(catalogName, item.id), { ES: trimmed });
    } catch (e) {
      showAlert("Error al guardar ES: " + e.message, "error");
    }
  };

  const saveEN = async () => {
    const trimmed = enDraft.trim();
    setEditingEN(false);
    if (trimmed === (item.EN || "")) return;
    try {
      await updateDoc(itemPath(catalogName, item.id), { EN: trimmed });
    } catch (e) {
      showAlert("Error al guardar EN: " + e.message, "error");
    }
  };

  const toggleActivo = async () => {
    try {
      await updateDoc(itemPath(catalogName, item.id), { activo: !activo });
    } catch (e) {
      showAlert("Error al cambiar estado: " + e.message, "error");
    }
  };

  // En modo soloEtiquetas la fila no es arrastrable ni tiene toggle/eliminar.
  const renderRow = (prov) => (
        <div
          ref={prov ? prov.innerRef : undefined}
          {...(prov ? prov.draggableProps : {})}
          className={`flex items-center gap-2 rounded-lg border border-zinc-200 px-2 py-1.5 ${
            activo ? "bg-white" : "bg-zinc-50 opacity-70"
          }`}
        >
          {!soloEtiquetas && (
            <button
              type="button"
              {...(prov ? prov.dragHandleProps : {})}
              className="cursor-grab text-zinc-400 hover:text-zinc-600"
              title="Arrastrar para reordenar"
            >
              <Bars3Icon className="h-5 w-5" />
            </button>
          )}

          <span className="text-xs text-zinc-400 w-8 shrink-0 tabular-nums">
            #{item.id}
          </span>

          {/* ES field */}
          <div className="flex-1 flex items-center gap-1 min-w-0">
            <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wide w-6 shrink-0">
              ES
            </span>
            {editingES ? (
              <>
                <input
                  autoFocus
                  className="flex-1 rounded border border-zinc-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                  value={esDraft}
                  onChange={(e) => setEsDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveES();
                    if (e.key === "Escape") {
                      setEsDraft(item.ES || "");
                      setEditingES(false);
                    }
                  }}
                  onBlur={saveES}
                />
              </>
            ) : (
              <button
                type="button"
                onClick={() => setEditingES(true)}
                className="flex-1 text-left text-sm truncate text-zinc-900 hover:bg-zinc-50 px-1 rounded"
              >
                {item.ES || (
                  <span className="italic text-zinc-400">Sin traducción</span>
                )}
              </button>
            )}
          </div>

          {/* EN field */}
          <div className="flex-1 flex items-center gap-1 min-w-0">
            <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wide w-6 shrink-0">
              EN
            </span>
            {editingEN ? (
              <input
                autoFocus
                className="flex-1 rounded border border-zinc-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                value={enDraft}
                onChange={(e) => setEnDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveEN();
                  if (e.key === "Escape") {
                    setEnDraft(item.EN || "");
                    setEditingEN(false);
                  }
                }}
                onBlur={saveEN}
              />
            ) : (
              <button
                type="button"
                onClick={() => setEditingEN(true)}
                className="flex-1 text-left text-sm truncate text-zinc-700 hover:bg-zinc-50 px-1 rounded"
              >
                {item.EN || (
                  <span className="italic text-zinc-400">Sin traducción</span>
                )}
              </button>
            )}
          </div>

          {/* Activo toggle (solo lectura en modo soloEtiquetas) */}
          {soloEtiquetas ? (
            <span
              className={`rounded px-2 py-0.5 text-xs font-medium shrink-0 ${
                activo
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-amber-100 text-amber-800"
              }`}
            >
              {activo ? "Activo" : "Inactivo"}
            </span>
          ) : (
            <button
              type="button"
              onClick={toggleActivo}
              className={`rounded px-2 py-0.5 text-xs font-medium shrink-0 ${
                activo
                  ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                  : "bg-amber-100 text-amber-800 hover:bg-amber-200"
              }`}
            >
              {activo ? "Activo" : "Inactivo"}
            </button>
          )}

          {!soloEtiquetas && (
            <button
              type="button"
              onClick={() => onRequestDelete(item)}
              className="text-zinc-400 hover:text-red-500 shrink-0"
              title="Eliminar"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          )}
        </div>
  );

  if (soloEtiquetas) return renderRow(null);

  return (
    <Draggable draggableId={`${catalogName}-${item.id}`} index={index}>
      {(prov) => renderRow(prov)}
    </Draggable>
  );
};

const CatalogSection = ({ catalog, items, onRequestDelete }) => {
  const [newES, setNewES] = useState("");
  const [newEN, setNewEN] = useState("");
  const [adding, setAdding] = useState(false);
  const soloEtiquetas = !!catalog.soloEtiquetas;

  const activeCount = items.filter(isItemActivo).length;
  const inactiveCount = items.length - activeCount;

  const handleAdd = async () => {
    const es = newES.trim();
    if (!es) return;
    setAdding(true);
    try {
      const id = nextNumericId(items);
      const orden =
        items.length > 0
          ? Math.max(...items.map((i) => (typeof i.orden === "number" ? i.orden : 0))) + 1
          : 1;
      await setDoc(itemPath(catalog.name, id), {
        ES: es,
        EN: newEN.trim(),
        key: slugifyKey(es),
        orden,
        activo: true,
      });
      setNewES("");
      setNewEN("");
    } catch (e) {
      showAlert("Error al agregar: " + e.message, "error");
    } finally {
      setAdding(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const src = result.source.index;
    const dst = result.destination.index;
    if (src === dst) return;
    const reordered = [...items];
    const [moved] = reordered.splice(src, 1);
    reordered.splice(dst, 0, moved);
    const batch = writeBatch(db);
    reordered.forEach((item, i) => {
      batch.update(itemPath(catalog.name, item.id), { orden: i + 1 });
    });
    try {
      await batch.commit();
    } catch (e) {
      showAlert("Error al reordenar: " + e.message, "error");
    }
  };

  const badge = (
    <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-700">
      {activeCount} activos
      {inactiveCount > 0 && (
        <span className="text-amber-700">· {inactiveCount} inactivos</span>
      )}
    </span>
  );

  return (
    <SectionShell
      id={catalog.name}
      title={catalog.label}
      icon={<catalog.icon className="h-5 w-5" />}
      badge={badge}
    >
      {/* Hint modo soloEtiquetas */}
      {soloEtiquetas && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <LockClosedIcon className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Solo edición de etiquetas — estos catálogos definen campos de
            datos de los venues (los valores se guardan por item id), así que
            agregar, eliminar, reordenar o desactivar items podría corromper
            la información ya guardada.
          </span>
        </div>
      )}

      {/* Add row */}
      {!soloEtiquetas && (
        <div className="grid grid-cols-1 sm:grid-cols-[1fr,1fr,auto] gap-2 mb-4">
          <Input
            placeholder="ES — nombre en español"
            value={newES}
            onChange={(e) => setNewES(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAdd();
            }}
          />
          <Input
            placeholder="EN — nombre en inglés (opcional)"
            value={newEN}
            onChange={(e) => setNewEN(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAdd();
            }}
          />
          <Button color="dark" onClick={handleAdd} disabled={adding || !newES.trim()}>
            <PlusIcon className="h-4 w-4" />
            Agregar
          </Button>
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-sm text-zinc-400 italic py-4">Sin items todavía.</div>
      ) : soloEtiquetas ? (
        <div className="space-y-1.5">
          {items.map((item, idx) => (
            <CatalogItemRow
              key={item.id}
              item={item}
              index={idx}
              catalogName={catalog.name}
              soloEtiquetas
              onRequestDelete={(it) => onRequestDelete(catalog, it)}
            />
          ))}
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId={catalog.name}>
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="space-y-1.5"
              >
                {items.map((item, idx) => (
                  <CatalogItemRow
                    key={item.id}
                    item={item}
                    index={idx}
                    catalogName={catalog.name}
                    onRequestDelete={(it) => onRequestDelete(catalog, it)}
                  />
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </SectionShell>
  );
};

const CatalogosManager = () => {
  const [itemsByCatalog, setItemsByCatalog] = useState(() =>
    Object.fromEntries(CATALOGS.map((c) => [c.name, []])),
  );
  const [activeSection, setActiveSection] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  // { catalog, item, refs, scanning, error }

  // Subscribe to each catalog's items
  useEffect(() => {
    const unsubs = CATALOGS.map((catalog) => {
      const q = query(itemsCollection(catalog.name));
      return onSnapshot(
        q,
        (snap) => {
          const items = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => {
              const ao = typeof a.orden === "number" ? a.orden : Number.POSITIVE_INFINITY;
              const bo = typeof b.orden === "number" ? b.orden : Number.POSITIVE_INFINITY;
              return ao - bo;
            });
          setItemsByCatalog((prev) => ({ ...prev, [catalog.name]: items }));
        },
        (err) => {
          console.error(`Snapshot error in ${catalog.name}:`, err);
        },
      );
    });
    return () => unsubs.forEach((u) => u && u());
  }, []);

  // Scrollspy
  useEffect(() => {
    const ids = CATALOGS.map((c) => c.name);
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActiveSection(visible[0].target.id);
      },
      { rootMargin: "-15% 0px -70% 0px", threshold: 0 },
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleRequestDelete = useCallback((catalog, item) => {
    setDeleteCandidate({ catalog, item, refs: null, scanning: true, error: null });
    findVenueReferences(catalog, item.id)
      .then((refs) => {
        setDeleteCandidate((prev) =>
          prev && prev.item.id === item.id && prev.catalog.name === catalog.name
            ? { ...prev, refs, scanning: false }
            : prev,
        );
      })
      .catch((err) => {
        setDeleteCandidate((prev) =>
          prev && prev.item.id === item.id && prev.catalog.name === catalog.name
            ? { ...prev, scanning: false, error: err.message }
            : prev,
        );
      });
  }, []);

  const closeDelete = () => setDeleteCandidate(null);

  const confirmDelete = async () => {
    if (!deleteCandidate || deleteCandidate.scanning) return;
    if (deleteCandidate.refs && deleteCandidate.refs.length > 0) return;
    const { catalog, item } = deleteCandidate;
    try {
      await deleteDoc(itemPath(catalog.name, item.id));
      setDeleteCandidate(null);
    } catch (e) {
      showAlert("Error al eliminar: " + e.message, "error");
    }
  };

  const totals = useMemo(() => {
    let active = 0;
    let inactive = 0;
    Object.values(itemsByCatalog).forEach((items) => {
      items.forEach((it) => (isItemActivo(it) ? active++ : inactive++));
    });
    return { active, inactive, total: active + inactive };
  }, [itemsByCatalog]);

  const blocked =
    deleteCandidate && !deleteCandidate.scanning && deleteCandidate.refs && deleteCandidate.refs.length > 0;

  return (
    <div className="container mx-auto p-4 pb-16 max-lg:px-0">
      {/* Header card (mirrors VenuePage) */}
      <div className="rounded-2xl border border-zinc-200 bg-white shadow-sm mb-6 p-5">
        <div className="text-xs uppercase tracking-wide text-zinc-400">
          Administración
        </div>
        <Heading level={2}>Catálogos de Venue</Heading>
        <p className="text-sm text-zinc-500 mt-1 max-w-3xl">
          Edita las listas que los venues ven al completar su perfil. Renombrar
          o reordenar es seguro; desactivar oculta un item para nuevas
          selecciones sin tocar la información de los venues que ya lo
          marcaron. <strong className="text-zinc-700">Eliminar</strong> está
          bloqueado si algún venue lo referencia.
        </p>
        <div className="mt-3 text-xs text-zinc-500">
          {CATALOGS.length} catálogos · {totals.total} items totales ·{" "}
          <span className="text-emerald-700">{totals.active} activos</span>
          {totals.inactive > 0 && (
            <>
              {" · "}
              <span className="text-amber-700">
                {totals.inactive} inactivos
              </span>
            </>
          )}
        </div>
      </div>

      <div className="flex gap-8">
        {/* Sidebar TOC (mirrors VenuePage) */}
        <aside className="hidden lg:block w-56 shrink-0">
          <nav className="sticky top-6 space-y-1">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400 mb-2">
              Catálogos
            </div>
            <ul className="space-y-0.5">
              {CATALOGS.map((c) => {
                const items = itemsByCatalog[c.name] || [];
                const active = activeSection === c.name;
                return (
                  <li key={c.name}>
                    <button
                      type="button"
                      onClick={() => scrollToSection(c.name)}
                      className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                        active
                          ? "bg-[#1b4d2e]/10 text-[#1b4d2e] font-medium"
                          : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
                      }`}
                    >
                      <c.icon
                        className={`h-4 w-4 shrink-0 ${
                          active ? "text-[#1b4d2e]" : "text-zinc-400"
                        }`}
                      />
                      <span className="flex-1 truncate">{c.label}</span>
                      <span className="text-xs text-zinc-400 tabular-nums">
                        {items.length}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0 space-y-4">
          {CATALOGS.map((catalog) => (
            <CatalogSection
              key={catalog.name}
              catalog={catalog}
              items={itemsByCatalog[catalog.name] || []}
              onRequestDelete={handleRequestDelete}
            />
          ))}
        </main>
      </div>

      {/* Hard-delete dialog */}
      <Dialog open={!!deleteCandidate} onClose={closeDelete}>
        <DialogTitle>
          {blocked ? "No se puede eliminar" : "Confirmar eliminación"}
        </DialogTitle>
        <DialogDescription>
          {deleteCandidate &&
            (deleteCandidate.scanning ? (
              <>Revisando si algún venue usa este item…</>
            ) : deleteCandidate.error ? (
              <>Error al revisar: {deleteCandidate.error}</>
            ) : blocked ? (
              <>
                <strong>{deleteCandidate.item.ES || deleteCandidate.item.key}</strong>{" "}
                lo usan {deleteCandidate.refs.length} venue
                {deleteCandidate.refs.length !== 1 ? "s" : ""}. Eliminarlo
                borraría silenciosamente esa info. Mejor desactívalo: deja de
                aparecer para nuevas selecciones pero los venues actuales lo
                conservan.
              </>
            ) : (
              <>
                Nadie está usando{" "}
                <strong>
                  {deleteCandidate.item.ES || deleteCandidate.item.key}
                </strong>
                . Puedes eliminarlo de forma permanente.
              </>
            ))}
        </DialogDescription>

        {blocked && (
          <DialogBody>
            <div className="max-h-60 overflow-y-auto rounded border border-zinc-200 divide-y divide-zinc-100">
              {deleteCandidate.refs.slice(0, 50).map((r) => (
                <div
                  key={r.venueId}
                  className="flex items-center justify-between px-3 py-2 text-sm"
                >
                  <span className="truncate font-medium text-zinc-800">
                    {r.venueName}
                  </span>
                  <span className="text-xs text-zinc-500 ml-3 shrink-0">
                    {r.fields.join(", ")}
                  </span>
                </div>
              ))}
              {deleteCandidate.refs.length > 50 && (
                <div className="px-3 py-2 text-xs text-zinc-500 italic">
                  …y {deleteCandidate.refs.length - 50} más
                </div>
              )}
            </div>
          </DialogBody>
        )}

        <DialogActions>
          <Button color="zinc" onClick={closeDelete}>
            {blocked ? "Cerrar" : "Cancelar"}
          </Button>
          {!blocked && deleteCandidate && !deleteCandidate.scanning && !deleteCandidate.error && (
            <Button color="red" onClick={confirmDelete}>
              Eliminar definitivamente
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CatalogosManager;
